/**
 * shared/vault-crypto.ts — the Vault key hierarchy (card VZik4YhoiIfz, split of
 * BCDYLuzx6orJ; spec V5SPOxJ3lCBq "key hierarchy" bullet).
 *
 * Why this exists separately from shared/at-rest.ts: at-rest is a WORKER-held
 * root key (PEAR_AT_REST_KEY) and says so in its own header — a compromise of
 * the worker secret reads couple data. A vault cannot be that. Here the root
 * key never exists on the server in plaintext: it is generated in the browser,
 * wrapped per member by that member's own passphrase-derived KEK, and only the
 * WRAP is ever stored. The server sees salt + iv + ciphertext.
 *
 * Hierarchy (spec-locked 2026-10-20):
 *   passphrase --PBKDF2(salt, iters)--> KEK --AES-GCM--> couple root key
 *   root key --AES-GCM(iv, aad=itemId)--> item ciphertext (per-item random IV)
 * One root key per couple; each partner wraps it with THEIR OWN passphrase and
 * salt, so unlock is independent per member (neither passphrase opens the
 * other's wrap) and both reach the same key.
 *
 * The vault passphrase is deliberately NOT the login password: the login
 * password is seen by the server (PBKDF2 in worker/src/api/auth.ts), so it
 * cannot be a content key without OPAQUE/PAKE.
 *
 * Framing: `pv:v1:` + base64( iv(12) || ct ). The prefix is the single source
 * of truth for "is this sealed" — same shape as at-rest's `enc:v1:`, so a
 * reader can tell the two regimes apart by looking at the bytes.
 *
 * WebCrypto only (no node:crypto): this module runs in the browser, in workerd
 * (nodejs_compat exposes the same globals) and in the node probe unchanged.
 *
 * Errors are named, not vague: `vault_bad_passphrase` is the one the unlock UI
 * explains to a person; `vault_bad_seal` means the ciphertext or the AAD did
 * not match (wrong item id, tampered bytes, wrong root key).
 */

const te = new TextEncoder();
const td = new TextDecoder();

/** Sealed-value prefix. Anything else is plaintext by definition. */
export const VAULT_PREFIX = 'pv:v1:';

/**
 * PBKDF2-SHA256 iterations for the vault passphrase (OWASP 2023 floor for
 * PBKDF2-SHA256). High enough that an offline guess is expensive, low enough
 * that a phone unlock stays interactive (~100ms class).
 */
export const VAULT_PBKDF2_ITERATIONS = 310_000;

const SALT_LEN = 16;
const IV_LEN = 12;
const ROOT_KEY_LEN = 32;
const GCM_TAG_LEN = 16;

/** One member's wrapped copy of the couple root key. Stored server-side. */
export interface VaultMemberWrap {
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  /** base64, per member — two partners never share a salt. */
  salt: string;
  /** base64 AES-GCM iv for the root-key wrap. */
  iv: string;
  /** base64 ciphertext of the root key. */
  ct: string;
}

function b64encode(bytes: Uint8Array): string {
  let s = '';
  const CHUNK = 0x8000; // avoid blowing the argument list on photo-sized input
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

function b64decode(text: string): Uint8Array<ArrayBuffer> {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concatBytes(a: Uint8Array<ArrayBuffer>, b: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/** The per-couple root key, as base64. Lives client-side only. */
export function createVaultRootKey(): string {
  return b64encode(crypto.getRandomValues(new Uint8Array(ROOT_KEY_LEN)));
}

/** True when a stored value carries the vault framing (never decrypts). */
export function isVaultSealed(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(VAULT_PREFIX);
}

async function kekFor(passphrase: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', te.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function rootKeyFor(rootKeyB64: string): Promise<CryptoKey> {
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = b64decode(rootKeyB64);
  } catch {
    // atob rejects non-base64 with a bare "Invalid character" — a malformed
    // key is a named condition, not a transport accident.
    throw new Error('vault_bad_root_key');
  }
  if (raw.length !== ROOT_KEY_LEN) throw new Error('vault_bad_root_key');
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/**
 * Wrap the couple root key under this member's passphrase. Random salt per
 * call, so enrolling the same passphrase twice still yields two independent
 * wraps (the re-issue path in V5 relies on that).
 */
export async function wrapVaultRootKey(rootKeyB64: string, passphrase: string): Promise<VaultMemberWrap> {
  if (!passphrase) throw new Error('vault_no_passphrase');
  await rootKeyFor(rootKeyB64); // reject a malformed root key before deriving
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
  const kek = await kekFor(passphrase, salt, VAULT_PBKDF2_ITERATIONS);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, kek, b64decode(rootKeyB64)),
  );
  return {
    kdf: 'PBKDF2-SHA256',
    iterations: VAULT_PBKDF2_ITERATIONS,
    salt: b64encode(salt),
    iv: b64encode(iv),
    ct: b64encode(ct),
  };
}

/**
 * Unwrap the root key with a passphrase. A wrong passphrase fails the GCM tag
 * and surfaces as `vault_bad_passphrase` — the one error the unlock screen is
 * required to explain in plain language (no key material echoed back).
 */
export async function unwrapVaultRootKey(wrap: VaultMemberWrap, passphrase: string): Promise<string> {
  if (!wrap || wrap.kdf !== 'PBKDF2-SHA256' || !wrap.salt || !wrap.iv || !wrap.ct) {
    throw new Error('vault_bad_wrap');
  }
  if (!passphrase) throw new Error('vault_no_passphrase');
  const kek = await kekFor(passphrase, b64decode(wrap.salt), wrap.iterations);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64decode(wrap.iv) },
      kek,
      b64decode(wrap.ct),
    );
    if (pt.byteLength !== ROOT_KEY_LEN) throw new Error('vault_bad_wrap');
    return b64encode(new Uint8Array(pt));
  } catch (e) {
    if (e instanceof Error && e.message === 'vault_bad_wrap') throw e;
    throw new Error('vault_bad_passphrase');
  }
}

/* -------------------------------------------------------------------------
   Recovery (card 1CyNguq35PbV, V5 of BCDYLuzx6orJ)

   Spec V5SPOxJ3lCBq §3: a third wrap of the SAME root key exists for recovery,

     KEK_recovery = HKDF(recovery code, salt = coupleId, info = 'pear-recovery-v1')

   and there is no server escrow. The code is 100 bits of randomness printed once
   at enrolment, so a slow KDF buys nothing here (unlike a human passphrase) and
   HKDF is the spec's choice: the code IS the entropy.

   Consequences this module must keep true:
     - the server stores only the wrap — it cannot open it and holds no escrow;
     - the code is the ONLY thing that opens it, so a wrong code is a named
       failure (`vault_bad_recovery_code`) and never a partial success;
     - the wrap is bound to the couple via the HKDF salt, so a wrap lifted from
       one couple's row cannot be opened under another couple's id.
   ------------------------------------------------------------------------- */

/** Crockford base32: no I, L, O or U, so a transcribed code cannot be misread. */
export const VAULT_RECOVERY_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** 20 symbols = 100 bits; grouped 4-4-4-4-4 for reading aloud. */
export const VAULT_RECOVERY_LENGTH = 20;
export const VAULT_RECOVERY_GROUP = 4;
/** HKDF info string — pinned by the spec. Changing it orphans every issued code. */
export const VAULT_RECOVERY_INFO = 'pear-recovery-v1';

/** The recovery wrap as stored server-side. Also non-secret BY CONSTRUCTION. */
export interface VaultRecoveryWrap {
  kdf: 'HKDF-SHA256';
  /** base64 of the HKDF salt (the couple id) — stored so a reader can check it. */
  salt: string;
  iv: string;
  ct: string;
}

/**
 * Mint a recovery code. 32 divides 256, so `byte % 32` is unbiased with no
 * rejection loop. Returned grouped; `normalizeVaultRecoveryCode` accepts either
 * shape, so a person can type it with spaces or dashes.
 */
export function generateVaultRecoveryCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(VAULT_RECOVERY_LENGTH));
  const raw = Array.from(bytes, (b) => VAULT_RECOVERY_ALPHABET[b % VAULT_RECOVERY_ALPHABET.length]).join('');
  const groups: string[] = [];
  for (let i = 0; i < raw.length; i += VAULT_RECOVERY_GROUP) {
    groups.push(raw.slice(i, i + VAULT_RECOVERY_GROUP));
  }
  return groups.join('-');
}

/**
 * Canonical form of a typed code: upper case, separators dropped, and the
 * Crockford confusables folded (I/L -> 1, O -> 0). Anything that is still not a
 * 20-symbol base32 string is rejected rather than passed to HKDF, because HKDF
 * happily derives from any string and a typo would then read as a wrong code.
 */
export function normalizeVaultRecoveryCode(input: string): string {
  const folded = String(input ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');
  if (folded.length !== VAULT_RECOVERY_LENGTH) throw new Error('vault_bad_recovery_code');
  for (const ch of folded) {
    if (!VAULT_RECOVERY_ALPHABET.includes(ch)) throw new Error('vault_bad_recovery_code');
  }
  return folded;
}

async function recoveryKekFor(recoveryCode: string, coupleId: string): Promise<CryptoKey> {
  const code = normalizeVaultRecoveryCode(recoveryCode);
  if (!coupleId) throw new Error('vault_no_couple');
  const material = await crypto.subtle.importKey('raw', te.encode(code), 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: te.encode(coupleId),
      info: te.encode(VAULT_RECOVERY_INFO),
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Wrap the root key under the recovery code (third wrap beside the members'). */
export async function wrapVaultRootKeyWithRecovery(
  rootKeyB64: string,
  recoveryCode: string,
  coupleId: string,
): Promise<VaultRecoveryWrap> {
  await rootKeyFor(rootKeyB64); // reject a malformed root key before deriving
  const kek = await recoveryKekFor(recoveryCode, coupleId);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, kek, b64decode(rootKeyB64)),
  );
  return {
    kdf: 'HKDF-SHA256',
    salt: b64encode(te.encode(coupleId)),
    iv: b64encode(iv),
    ct: b64encode(ct),
  };
}

/**
 * Open the recovery wrap. A wrong code and a wrap belonging to another couple
 * both answer `vault_bad_recovery_code`: the caller learns "this code does not
 * open this vault" and nothing about which half failed.
 */
export async function unwrapVaultRootKeyWithRecovery(
  wrap: VaultRecoveryWrap,
  recoveryCode: string,
  coupleId: string,
): Promise<string> {
  if (!wrap || wrap.kdf !== 'HKDF-SHA256' || !wrap.salt || !wrap.iv || !wrap.ct) {
    throw new Error('vault_bad_recovery_wrap');
  }
  if (b64encode(te.encode(coupleId)) !== wrap.salt) throw new Error('vault_bad_recovery_code');
  const kek = await recoveryKekFor(recoveryCode, coupleId);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64decode(wrap.iv) },
      kek,
      b64decode(wrap.ct),
    );
    if (pt.byteLength !== ROOT_KEY_LEN) throw new Error('vault_bad_recovery_wrap');
    return b64encode(new Uint8Array(pt));
  } catch (e) {
    if (e instanceof Error && e.message === 'vault_bad_recovery_wrap') throw e;
    throw new Error('vault_bad_recovery_code');
  }
}

/**
 * Seal one item's plaintext under the root key. AAD = item id, so a ciphertext
 * cannot be moved to a different row (or between couples' rows) and still open.
 */
export async function sealVaultItem(rootKeyB64: string, plaintext: string, aad: string): Promise<string> {
  const key = await rootKeyFor(rootKeyB64);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: te.encode(aad) },
      key,
      te.encode(plaintext),
    ),
  );
  return VAULT_PREFIX + b64encode(concatBytes(iv, ct));
}

/** Open a sealed item. Wrong key / wrong AAD / tampered bytes -> vault_bad_seal. */
export async function openVaultItem(rootKeyB64: string, sealed: string, aad: string): Promise<string> {
  if (!isVaultSealed(sealed)) throw new Error('vault_bad_format');
  const raw = b64decode(sealed.slice(VAULT_PREFIX.length));
  // An empty plaintext is a legitimate 28-byte payload (iv 12 + tag 16), so
  // only a payload SHORTER than that can be structurally wrong.
  if (raw.length < IV_LEN + GCM_TAG_LEN) throw new Error('vault_bad_format');
  const key = await rootKeyFor(rootKeyB64);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: raw.subarray(0, IV_LEN), additionalData: te.encode(aad) },
      key,
      raw.subarray(IV_LEN),
    );
    return td.decode(pt);
  } catch {
    throw new Error('vault_bad_seal');
  }
}
